import { useState, useCallback } from "react";
import { VaultFile } from "@/types";
import { uploadFile as apiUploadFile } from "@/services/storageManager";

export interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
  result?: VaultFile;
}

export function useUpload(folderId: string | null, onSuccess?: () => void) {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = useCallback(
    async (item: UploadItem): Promise<VaultFile | null> => {
      try {
        const result = await apiUploadFile(
          item.file,
          folderId,
          [], // tags
          (progress) => {
            setUploads((prev) =>
              prev.map((u) =>
                u.id === item.id ? { ...u, progress } : u
              )
            );
          }
        );

        setUploads((prev) =>
          prev.map((u) =>
            u.id === item.id
              ? { ...u, progress: 100, status: "completed", result }
              : u
          )
        );

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Upload failed";
        setUploads((prev) =>
          prev.map((u) =>
            u.id === item.id
              ? { ...u, status: "error", error: errorMessage }
              : u
          )
        );
        return null;
      }
    },
    [folderId]
  );

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const newUploads: UploadItem[] = fileArray.map((file): UploadItem => ({
        id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        progress: 0,
        status: "pending",
      }));

      if (newUploads.length === 0) return;

      setUploads((prev) => [...prev, ...newUploads]);
      setIsUploading(true);

      // Process uploads sequentially
      for (const item of newUploads) {
        setUploads((prev) =>
          prev.map((u) => (u.id === item.id ? { ...u, status: "uploading" } : u))
        );

        await uploadFile(item);
      }

      setIsUploading(false);
      onSuccess?.();

      // Auto-clear completed uploads after delay
      setTimeout(() => {
        setUploads((prev) => prev.filter((u) => u.status !== "completed"));
      }, 3000);
    },
    [uploadFile, onSuccess]
  );

  const cancelUpload = useCallback((uploadId: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== uploadId));
  }, []);

  const clearUploads = useCallback(() => {
    setUploads([]);
    setIsUploading(false);
  }, []);

  const activeUploads = uploads.filter(
    (u) => u.status === "pending" || u.status === "uploading"
  );
  const completedUploads = uploads.filter((u) => u.status === "completed");
  const failedUploads = uploads.filter((u) => u.status === "error");

  return {
    uploads,
    activeUploads,
    completedUploads,
    failedUploads,
    isUploading,
    addFiles,
    cancelUpload,
    clearUploads,
  };
}
